import React from "react";
import { Popover, Tooltip } from "antd";
import OrderDTO from "@/dto/order/OrderDTO";
import OrderTag from "@/enum/OrderTag";
import OrderTagWrap from "../OrderTagWrap";
import ShopDTO from "@/dto/shop/ShopDTO";
import OrderStatus from "@/enum/OrderStatus";
import ShopType from "@/enum/ShopType";

/**
 * 抖店平台标签组件
 * 包含所有抖店相关的标签逻辑
 */
export default function useDoudianTagIcons(order: OrderDTO, orderTagSet: Set<OrderTag>, shop: ShopDTO) {
    const icons = [];

    if (orderTagSet.has(OrderTag.DOUDIAN_HOME_DELIVERY)) {
        icons.push(
            <OrderTagWrap
                className="order-icon icon-important"
                key={OrderTag.DOUDIAN_HOME_DELIVERY}
                orderTag={OrderTag.DOUDIAN_HOME_DELIVERY}
            />,
        );
    }

    if (orderTagSet.has(OrderTag.DOUDIAN_HOME_DELIVERY_RECOMMENDED)) {
        icons.push(
            <OrderTagWrap
                className="order-icon icon-important"
                key={OrderTag.DOUDIAN_HOME_DELIVERY_RECOMMENDED}
                orderTag={OrderTag.DOUDIAN_HOME_DELIVERY_RECOMMENDED}
            />,
        );
    }

    if (orderTagSet.has(OrderTag.DOUBLE_ADDRESS)) {
        icons.push(
            <OrderTagWrap
                className="order-icon icon-important"
                key={OrderTag.DOUBLE_ADDRESS}
                orderTag={OrderTag.DOUBLE_ADDRESS}
            />,
        );
    }

    if (orderTagSet.has(OrderTag.SHIPPING_DELAY)) {
        /** 临时兼容抖店（2023.07.07群上反馈,前端兼容） */
        /** 可以先让前端处理下，抖店非待发货订单不用展示这个标签，如果对其他用户有影响的话我们也可以快速回滚 */

        if (shop?.type === ShopType.DOUDIAN) {
            if (order.status === OrderStatus.PENDING_SHIPMENT) {
                if (order.shippingDeadline) {
                    icons.push(
                        <Tooltip title={`${order.shippingDeadline}前发货`} key={OrderTag.SHIPPING_DELAY}>
                            <OrderTagWrap className="order-icon icon-unimportant" orderTag={OrderTag.SHIPPING_DELAY} />
                        </Tooltip>,
                    );
                } else {
                    icons.push(
                        <OrderTagWrap
                            className="order-icon icon-unimportant"
                            key={OrderTag.SHIPPING_DELAY}
                            orderTag={OrderTag.SHIPPING_DELAY}
                        />,
                    );
                }
            }
        }
    }

    if (orderTagSet.has(OrderTag.DOUDIAN_SHIP_BY_SF)) {
        icons.push(
            <Tooltip
                title="商家已选择本商品承诺顺丰包邮。为保障消费者体验，本商品请选择顺丰速运发货，否则会扣除10元货款赔付给消费者"
                key={OrderTag.DOUDIAN_SHIP_BY_SF}
            >
                <OrderTagWrap
                    className="order-icon icon-important"
                    style={{ whiteSpace: "nowrap" }}
                    orderTag={OrderTag.DOUDIAN_SHIP_BY_SF}
                />
            </Tooltip>,
        );
    }

    if (orderTagSet.has(OrderTag.DOUDIAN_PRIORITY_DELIVERY)) {
        icons.push(
            <OrderTagWrap
                className="order-icon icon-important"
                key={OrderTag.DOUDIAN_PRIORITY_DELIVERY}
                orderTag={OrderTag.DOUDIAN_PRIORITY_DELIVERY}
            />,
        );
    }

    if (orderTagSet.has(OrderTag.DOUDIAN_CONSOLIDATION_XINJIANG)) {
        icons.push(
            <Popover
                content={
                    <img src="https://img.alicdn.com/imgextra/i4/54458674/O1CN01vdp36P2DwkXIeU8Gu_!!54458674.png" />
                }
                key={OrderTag.DOUDIAN_CONSOLIDATION_XINJIANG}
            >
                <OrderTagWrap
                    className="order-icon icon-unimportant"
                    orderTag={OrderTag.DOUDIAN_CONSOLIDATION_XINJIANG}
                />
            </Popover>,
        );
    }

    if (orderTagSet.has(OrderTag.DOUDIAN_CONSOLIDATION_MERGE)) {
        icons.push(
            <Tooltip
                key={OrderTag.DOUDIAN_CONSOLIDATION_MERGE}
                title="合包发货订单指发货到集运仓后由平台判断是否合并发给买家。如买家申请售后时请先确保成功拦截物流后才同意售后，否则可能会造成损失"
            >
                <OrderTagWrap className="order-icon icon-unimportant" orderTag={OrderTag.DOUDIAN_CONSOLIDATION_MERGE} />
            </Tooltip>,
        );
    }

    if (orderTagSet.has(OrderTag.DOUDIAN_DELIVERY_APPOINTMENT)) {
        icons.push(
            <OrderTagWrap
                className="order-icon icon-important"
                key={OrderTag.DOUDIAN_DELIVERY_APPOINTMENT}
                orderTag={OrderTag.DOUDIAN_DELIVERY_APPOINTMENT}
            />,
        );
    }

    if (orderTagSet.has(OrderTag.DOUDIAN_APPOINTMENT_DELIVERY)) {
        icons.push(
            <OrderTagWrap
                className="order-icon icon-important"
                key={OrderTag.DOUDIAN_APPOINTMENT_DELIVERY}
                orderTag={OrderTag.DOUDIAN_APPOINTMENT_DELIVERY}
            />,
        );
    }

    if (orderTagSet.has(OrderTag.DOUDIAN_APPOINTMENT_ARRIVAL_V2)) {
        icons.push(
            <OrderTagWrap
                className="order-icon icon-important"
                key={OrderTag.DOUDIAN_APPOINTMENT_ARRIVAL_V2}
                orderTag={OrderTag.DOUDIAN_APPOINTMENT_ARRIVAL_V2}
            />,
        );
    }

    if (orderTagSet.has(OrderTag.DOUDIAN_PROMISE_ARRIVAL)) {
        icons.push(
            <OrderTagWrap
                className="order-icon icon-important"
                key={OrderTag.DOUDIAN_PROMISE_ARRIVAL}
                orderTag={OrderTag.DOUDIAN_PROMISE_ARRIVAL}
            />,
        );
    }

    if (orderTagSet.has(OrderTag.DOUDIAN_APPOINTMENT_ARRIVAL)) {
        icons.push(
            <Tooltip
                key={OrderTag.DOUDIAN_APPOINTMENT_ARRIVAL}
                title={
                    <div>
                        该抖店为承诺日达订单，对送达时效有要求，仅支持使用京东、顺丰打印发货，逾期发货可能会产生赔付风险，建议尽快发货
                    </div>
                }
            >
                <OrderTagWrap className="order-icon icon-important" orderTag={OrderTag.DOUDIAN_APPOINTMENT_ARRIVAL} />
            </Tooltip>,
        );
    }

    if (orderTagSet.has(OrderTag.DOUDIAN_REMOTE_DIRECT)) {
        icons.push(
            <OrderTagWrap
                className="order-icon icon-important"
                key={OrderTag.DOUDIAN_REMOTE_DIRECT}
                orderTag={OrderTag.DOUDIAN_REMOTE_DIRECT}
            />,
        );
    }

    if (orderTagSet.has(OrderTag.DOUDIAN_CONSOLIDATION_XIZANG)) {
        icons.push(
            <Popover
                content={
                    <img src="https://img.alicdn.com/imgextra/i4/54458674/O1CN01vdp36P2DwkXIeU8Gu_!!54458674.png" />
                }
                key={OrderTag.DOUDIAN_CONSOLIDATION_XIZANG}
            >
                <OrderTagWrap
                    className="order-icon icon-unimportant"
                    orderTag={OrderTag.DOUDIAN_CONSOLIDATION_XIZANG}
                />
            </Popover>,
        );
    }

    if (orderTagSet.has(OrderTag.DOUDIAN_ORDER_SUPPLY)) {
        icons.push(
            <OrderTagWrap
                className="order-icon icon-important"
                orderTag={OrderTag.DOUDIAN_ORDER_SUPPLY}
                key={OrderTag.DOUDIAN_ORDER_SUPPLY}
            />,
        );
    }

    if (orderTagSet.has(OrderTag.DOUDIAN_GIFT_LATE_DELIVERY)) {
        icons.push(
            <Tooltip
                key={OrderTag.DOUDIAN_GIFT_LATE_DELIVERY}
                title="该订单包含赠品后发商品项，可点击“详情”查看赠品发货时效"
            >
                <OrderTagWrap className="order-icon icon-unimportant" orderTag={OrderTag.DOUDIAN_GIFT_LATE_DELIVERY} />
            </Tooltip>,
        );
    }

    if (orderTagSet.has(OrderTag.DOUDIAN_PAID_SF_EXPRESS)) {
        icons.push(
            <OrderTagWrap
                className="order-icon icon-important"
                orderTag={OrderTag.DOUDIAN_PAID_SF_EXPRESS}
                key={OrderTag.DOUDIAN_PAID_SF_EXPRESS}
            />,
        );
    }

    if (orderTagSet.has(OrderTag.DOUDIAN_PAID_SF_EXPRESS_TK)) {
        icons.push(
            <Tooltip key={OrderTag.DOUDIAN_PAID_SF_EXPRESS_TK} title="加价发顺丰特快">
                <OrderTagWrap className="order-icon icon-important" orderTag={OrderTag.DOUDIAN_PAID_SF_EXPRESS_TK} />
            </Tooltip>,
        );
    }

    if (orderTagSet.has(OrderTag.DOUDIAN_HIGH_QUALITY_EXPRESS)) {
        icons.push(
            <OrderTagWrap
                className="order-icon icon-important"
                key={OrderTag.DOUDIAN_HIGH_QUALITY_EXPRESS}
                orderTag={OrderTag.DOUDIAN_HIGH_QUALITY_EXPRESS}
            />,
        );
    }

    if (orderTagSet.has(OrderTag.DOUDIAN_BUY_FREQUENCY)) {
        icons.push(
            <Tooltip key={OrderTag.DOUDIAN_BUY_FREQUENCY} title="近30天用户在平台购买频率高">
                <OrderTagWrap className="order-icon icon-unimportant" orderTag={OrderTag.DOUDIAN_BUY_FREQUENCY} />
            </Tooltip>,
        );
    }

    if (orderTagSet.has(OrderTag.DOUDIAN_PRIORITY_SERVICE)) {
        icons.push(
            <Tooltip key={OrderTag.DOUDIAN_PRIORITY_SERVICE} title="近30天用户在平台退款频率低">
                <OrderTagWrap className="order-icon icon-unimportant" orderTag={OrderTag.DOUDIAN_PRIORITY_SERVICE} />
            </Tooltip>,
        );
    }

    if (orderTagSet.has(OrderTag.DOUDIAN_SHOP_REGULAR_CUSTOMER)) {
        icons.push(
            <Tooltip key={OrderTag.DOUDIAN_SHOP_REGULAR_CUSTOMER} title="历史在店铺有下过单的用户">
                <OrderTagWrap
                    className="order-icon icon-unimportant"
                    orderTag={OrderTag.DOUDIAN_SHOP_REGULAR_CUSTOMER}
                />
            </Tooltip>,
        );
    }

    if (orderTagSet.has(OrderTag.DOUDIAN_TOMORROW_RECEIPT)) {
        icons.push(
            <Tooltip
                key={OrderTag.DOUDIAN_TOMORROW_RECEIPT}
                title={
                    <span>
                        <span>当前线路已开通明日达服务，请优先发货保障订单明日达，超时未送达会产生赔付成本</span>
                        <a
                            href="https://fxg.jinritemai.com/ffa/logistics-project/logistics-service/main?btm_ppre=a2427.b76571.c902327.d871297&btm_pre=a2427.b76571.c902327.d871297&btm_show_id=872857cb-259e-4450-a259-3b095e04c132"
                            target="_blank"
                            className="ml3"
                        >
                            抖店-订单-订单管理-常用工具-明日达服务
                        </a>
                    </span>
                }
            >
                <OrderTagWrap className="order-icon icon-important" orderTag={OrderTag.DOUDIAN_TOMORROW_RECEIPT} />
            </Tooltip>,
        );
    }

    if (orderTagSet.has(OrderTag.DOUDIAN_SHOP_INSTALLATION)) {
        icons.push(
            <Tooltip
                key={OrderTag.DOUDIAN_SHOP_INSTALLATION}
                title="由商家提供安装服务，商家需要在物流签收后24小时内与消费者主动预约并按约上门"
            >
                <OrderTagWrap className="order-icon icon-important" orderTag={OrderTag.DOUDIAN_SHOP_INSTALLATION} />
            </Tooltip>,
        );
    }

    if (orderTagSet.has(OrderTag.DOUDIAN_SPRING_FESTIVAL_DELAY_SHIPPING)) {
        icons.push(
            <OrderTagWrap
                className="order-icon icon-unimportant"
                orderTag={OrderTag.DOUDIAN_SPRING_FESTIVAL_DELAY_SHIPPING}
                key={OrderTag.DOUDIAN_SPRING_FESTIVAL_DELAY_SHIPPING}
            />,
        );
    }

    return icons;
}
